/**
 * API Client Configuration
 *
 * Axios instance with interceptors for authentication, error handling, logging,
 * and automatic token refresh.
 *
 * Uses dual-token system (15-minute access tokens + 7-day refresh tokens).
 *
 * IMPORTANT: When token refresh fails on a regular API call, this client dispatches
 * a 'auth:force-logout' CustomEvent instead of doing a hard redirect. The AuthContext
 * listens for this event and performs a controlled logout (clearing React state,
 * cleaning up timers, and letting ProtectedRoute redirect naturally).
 */

import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { APIErrorResponse } from '../types/api.types';
import type { TokenRefreshResponse } from '../types/auth.types';
import {
  getAccessToken,
  getRefreshToken,
  saveTokenPair,
  clearAllTokens,
  isTokenExpiringSoon,
} from '../lib/utils/token';
import { TOKEN_EXPIRY_BUFFER } from '../lib/constants';

// ============================================================================
// AUTH EVENT CONSTANTS
// ============================================================================

/**
 * Custom event dispatched when token refresh fails and the user must re-authenticate.
 * AuthContext listens for this event and performs controlled logout.
 */
export const AUTH_FORCE_LOGOUT_EVENT = 'auth:force-logout';

// ============================================================================
// CROSS-TAB AUTH SYNC (BroadcastChannel)
// ============================================================================

/**
 * Event types broadcast to other tabs for auth state coordination.
 * TOKEN_REFRESHED: a tab successfully refreshed tokens — others should sync state.
 * SESSION_REVOKED: a tab logged out — others should also log out.
 */
export const AUTH_TOKEN_REFRESHED_EVENT = 'TOKEN_REFRESHED';
export const AUTH_SESSION_REVOKED_EVENT = 'SESSION_REVOKED';

/**
 * BroadcastChannel for cross-tab auth synchronization.
 * Undefined in environments that don't support BroadcastChannel (e.g. old Safari).
 */
export let authSyncChannel: BroadcastChannel | null = null;
try {
  authSyncChannel = new BroadcastChannel('ketrics_auth_sync');
} catch {
  // BroadcastChannel not supported in this environment
}

// ============================================================================
// CROSS-TAB REFRESH LOCK
// ============================================================================

/**
 * localStorage key for refresh lock. Only one tab should hold this lock at a time.
 */
const REFRESH_LOCK_KEY = 'ketrics_refresh_lock';

/**
 * Time-to-live for the refresh lock. If a tab crashes/closes without releasing
 * the lock, other tabs will time out after this period and proceed.
 */
const REFRESH_LOCK_TTL_MS = 15000; // 15 seconds

/**
 * Attempt to acquire the refresh lock. Returns true if lock acquired, false
 * if another tab holds a fresh lock. Uses localStorage to coordinate across tabs.
 */
function tryAcquireRefreshLock(): boolean {
  const existing = localStorage.getItem(REFRESH_LOCK_KEY);
  if (existing) {
    const lockTimestamp = parseInt(existing, 10);
    if (Date.now() - lockTimestamp < REFRESH_LOCK_TTL_MS) {
      return false; // Another tab holds a fresh lock
    }
  }
  localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
  return true;
}

/**
 * Release the refresh lock so other tabs can acquire it.
 */
function releaseRefreshLock(): void {
  localStorage.removeItem(REFRESH_LOCK_KEY);
}

/**
 * Wait for another tab to refresh the token. Listens on BroadcastChannel for
 * TOKEN_REFRESHED event, or falls back to polling localStorage lock release
 * for environments without BroadcastChannel. Timeout is REFRESH_LOCK_TTL_MS.
 */
function waitForCrossTabRefresh(): Promise<string | null> {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve(getAccessToken()); // Stale lock expired — read whatever is in localStorage
    }, REFRESH_LOCK_TTL_MS);

    const cleanup = () => {
      clearTimeout(timeout);
      authSyncChannel?.removeEventListener('message', handler);
    };

    const handler = (event: MessageEvent) => {
      if (
        event.data.type === AUTH_TOKEN_REFRESHED_EVENT ||
        event.data.type === AUTH_SESSION_REVOKED_EVENT
      ) {
        cleanup();
        resolve(getAccessToken());
      }
    };

    if (authSyncChannel) {
      authSyncChannel.addEventListener('message', handler);
    } else {
      // Fallback: poll localStorage lock release for environments without BroadcastChannel
      const poll = setInterval(() => {
        if (!localStorage.getItem(REFRESH_LOCK_KEY)) {
          clearInterval(poll);
          clearTimeout(timeout);
          resolve(getAccessToken());
        }
      }, 100);
    }
  });
}

// ============================================================================
// AXIOS INSTANCE CREATION
// ============================================================================

/**
 * Create and configure Axios instance
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// TOKEN REFRESH STATE
// ============================================================================

/**
 * Track if a refresh is in progress to prevent concurrent refreshes
 */
let isRefreshing = false;

/**
 * Queue of requests waiting for token refresh.
 * Callback receives the new token on success, or null on failure.
 */
let refreshSubscribers: Array<(token: string | null) => void> = [];

/**
 * Subscribe to token refresh completion
 */
const subscribeToRefresh = (callback: (token: string | null) => void): void => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers with new token (success)
 */
const onRefreshComplete = (token: string): void => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Notify all subscribers that refresh failed
 */
const onRefreshFailed = (): void => {
  refreshSubscribers.forEach((callback) => callback(null));
  refreshSubscribers = [];
};

/**
 * Perform token refresh using direct axios (bypasses interceptors).
 *
 * Self-gating: if a refresh is already in progress (from ANY code path — timer or
 * interceptor), this function queues the caller as a subscriber and resolves when
 * the in-progress refresh completes. This prevents concurrent refreshes within the
 * same tab.
 *
 * Cross-tab locking: before making the HTTP call, attempts to acquire a
 * localStorage-based lock. Only the tab that acquires the lock makes the HTTP call;
 * other tabs wait on the BroadcastChannel for TOKEN_REFRESHED event, then read the
 * new token from localStorage. This prevents multiple tabs from sending the same
 * refresh token simultaneously, which would trigger token-reuse detection on the server.
 *
 * @returns New access token or null if refresh fails
 */
export const performTokenRefresh = async (): Promise<string | null> => {
  // ── Gate: if another refresh is already in flight IN THIS TAB, wait ──
  if (isRefreshing) {
    return new Promise<string | null>((resolve) => {
      const timeout = setTimeout(() => resolve(null), 10000);
      subscribeToRefresh((token) => {
        clearTimeout(timeout);
        resolve(token);
      });
    });
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log('[API] No refresh token available');
    return null;
  }

  // ── Cross-tab check: another tab may have already refreshed ──
  const currentToken = getAccessToken();
  if (currentToken && !isTokenExpiringSoon(currentToken, TOKEN_EXPIRY_BUFFER)) {
    console.log('[API] Token already refreshed (likely by another tab), skipping HTTP call');
    return currentToken;
  }

  // ── Cross-tab lock: prevent concurrent /auth/refresh calls from multiple tabs ──
  if (!tryAcquireRefreshLock()) {
    console.log('[API] Another tab is refreshing, waiting for result...');
    isRefreshing = true;
    try {
      return await waitForCrossTabRefresh();
    } finally {
      isRefreshing = false;
      onRefreshComplete(getAccessToken() ?? ''); // unblock any in-tab subscribers
    }
  }

  isRefreshing = true;
  try {
    // Recheck after acquiring lock (another tab may have just finished)
    const tokenAfterLock = getAccessToken();
    if (tokenAfterLock && !isTokenExpiringSoon(tokenAfterLock, TOKEN_EXPIRY_BUFFER)) {
      console.log('[API] Token refreshed while acquiring lock, skipping HTTP call');
      onRefreshComplete(tokenAfterLock);
      return tokenAfterLock;
    }

    console.log('[API] Refreshing tokens...');

    // Make refresh request directly to avoid interceptor loops
    const response = await axios.post<{ success: true; data: TokenRefreshResponse }>(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/refresh`,
      { refreshToken },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      }
    );

    const { accessToken, refreshToken: newRefreshToken } = response.data.data;

    // Save new tokens
    saveTokenPair(accessToken, newRefreshToken);

    // Notify waiting subscribers (other code paths waiting on isRefreshing)
    onRefreshComplete(accessToken);

    // Broadcast to other tabs so they can sync React state without re-refreshing
    authSyncChannel?.postMessage({ type: AUTH_TOKEN_REFRESHED_EVENT });

    console.log('[API] Token refresh successful');
    return accessToken;
  } catch (error) {
    console.error('[API] Token refresh failed:', error);
    onRefreshFailed();
    return null;
  } finally {
    isRefreshing = false;
    releaseRefreshLock();
  }
};

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  return getAccessToken();
};

/**
 * Remove authentication token from localStorage
 */
export const clearAuthToken = (): void => {
  clearAllTokens();
};

/**
 * Check if a URL is an auth endpoint that should be excluded from
 * automatic token refresh/retry logic.
 */
const isAuthEndpointUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/verify-login') ||
    url.includes('/auth/resend-login-code') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/password-reset') ||
    url.includes('/auth/sso/providers/') ||
    url.includes('/auth/sso/authorize') ||
    url.includes('/auth/sso/callback')
  );
};

// ============================================================================
// FORCE LOGOUT DEDUPLICATION
// ============================================================================

/**
 * Prevent dispatching multiple force-logout events in rapid succession.
 * Once a force-logout is dispatched, suppress further dispatches for 2 seconds.
 */
let forceLogoutDispatched = false;

const dispatchForceLogout = (): void => {
  if (forceLogoutDispatched) return;
  forceLogoutDispatched = true;

  console.log('[API] Dispatching force-logout event');
  window.dispatchEvent(new CustomEvent(AUTH_FORCE_LOGOUT_EVENT));

  // Reset after 2 seconds to allow future logouts (e.g., after re-login)
  setTimeout(() => {
    forceLogoutDispatched = false;
  }, 2000);
};

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

/**
 * Request interceptor: Add auth token, handle pre-emptive refresh
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token handling for auth endpoints (prevent loops)
    if (!isAuthEndpointUrl(config.url)) {
      let token = getAuthToken();

      // Check if we should refresh the token pre-emptively
      if (token && isTokenExpiringSoon(token, TOKEN_EXPIRY_BUFFER)) {
        console.log('[API] Access token expiring soon, refreshing pre-emptively...');

        // performTokenRefresh is self-gating: concurrent callers (timer, interceptor)
        // share a single in-flight request via isRefreshing + subscriber queue.
        const newToken = await performTokenRefresh();
        if (newToken) {
          token = newToken;
        } else {
          console.warn('[API] Pre-emptive refresh failed, continuing with existing token');
        }
      }

      // Add authorization header if token exists
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Log request in development mode
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR - ERROR HANDLING
// ============================================================================

/**
 * Extended config for retry tracking
 */
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

/**
 * Response interceptor: Handle errors globally, attempt token refresh on 401.
 *
 * Key design decisions:
 * - Auth endpoint 401s (login, refresh, logout, etc.) are passed through to the caller
 *   without any automatic retry or cleanup. The caller (e.g., AuthContext) handles them.
 * - Non-auth endpoint 401s trigger a token refresh attempt. If refresh fails,
 *   a custom event is dispatched so AuthContext can perform controlled logout.
 * - This avoids hard `window.location.href` redirects that bypass React state management.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development mode
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError<APIErrorResponse>) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Log error in development mode
    if (import.meta.env.DEV) {
      console.error('[API Response Error]', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // Handle 401 Unauthorized - attempt token refresh if using dual-token system
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // For auth endpoints, don't try to refresh or redirect - let the caller handle it.
      // This prevents authService.refresh() failures from triggering hard logouts.
      if (isAuthEndpointUrl(originalRequest.url)) {
        return Promise.reject(error);
      }

      if (getRefreshToken()) {
        originalRequest._retry = true;

        // performTokenRefresh is self-gating: if another caller is already refreshing,
        // this awaits its result instead of making a second /auth/refresh request.
        const newToken = await performTokenRefresh();
        if (newToken) {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return apiClient(originalRequest);
        }
      }

      // Token refresh failed or not available - dispatch event for AuthContext to handle.
      // AuthContext will clear state, timers, and ProtectedRoute will redirect to /login.
      dispatchForceLogout();
    }

    // Handle 403 Forbidden - Insufficient permissions
    if (error.response?.status === 403) {
      console.warn('[API] Forbidden: Insufficient permissions');
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.warn('[API] Resource not found:', error.config?.url);
    }

    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('[API] Server error:', error.response?.data);
    }

    // Handle network errors
    if (!error.response) {
      console.error('[API] Network error: No response received');
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// RESPONSE INTERCEPTOR - RETRY LOGIC
// ============================================================================

/**
 * Configure retry logic for specific error codes
 * Retries on network errors and 5xx server errors
 */
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

apiClient.interceptors.response.use(
  undefined,
  async (error: AxiosError) => {
    const config = error.config as ExtendedAxiosRequestConfig;

    // Only retry on network errors or 5xx server errors (not 401/403)
    const shouldRetry =
      !error.response || (error.response.status >= 500 && error.response.status < 600);

    if (shouldRetry && config && !config._retry) {
      config._retryCount = config._retryCount || 0;

      if (config._retryCount < MAX_RETRIES) {
        config._retryCount += 1;
        config._retry = true;

        // Exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, config._retryCount - 1);

        if (import.meta.env.DEV) {
          console.log(`[API] Retrying request (${config._retryCount}/${MAX_RETRIES}) after ${delay}ms`);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));

        return apiClient(config);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// EXPORT API CLIENT
// ============================================================================

export default apiClient;
